import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import { LogOut, Package, Clock, CheckCircle, Key, Plus, ClipboardList, Calculator, Sparkles, RefreshCw, Loader2, Menu, X, Home, Truck } from "lucide-react";
import MarkupCalculator from "../components/MarkupCalculator";
import OrderFormMultiPart from "../components/OrderFormMultiPart";
import OrdersList from "../components/OrdersList";
import ChangePasswordDialog from "../components/ChangePasswordDialog";
import DiagnosticGenerator from "../components/DiagnosticGenerator";
import TruckInventory from "../components/TruckInventory";

export default function TechDashboard({ user, onLogout }) {
  const [stats, setStats] = useState(null);
  const [orders, setOrders] = useState([]);
  const [activeTab, setActiveTab] = useState("orders");
  const [statusFilter, setStatusFilter] = useState("all");
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [refreshKey, setRefreshKey] = useState(Date.now());

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        const [ordersRes, statsRes] = await Promise.all([
          axios.get("/orders"),
          axios.get("/stats/dashboard")
        ]);
        setOrders(ordersRes.data || []);
        setStats(statsRes.data || { total: 0, pending: 0, ordered: 0, completed: 0 });
      } catch (error) {
        console.error("Failed to load data:", error);
        // Retry once after 2 seconds
        setTimeout(async () => {
          try {
            const [ordersRes, statsRes] = await Promise.all([
              axios.get("/orders"),
              axios.get("/stats/dashboard")
            ]);
            setOrders(ordersRes.data || []);
            setStats(statsRes.data || { total: 0, pending: 0, ordered: 0, completed: 0 });
          } catch (retryError) {
            toast.error("Connection issue - please refresh");
          }
        }, 2000);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, []);

  const refreshAll = async () => {
    try {
      setLoading(true);
      const [ordersRes, statsRes] = await Promise.all([
        axios.get("/orders"),
        axios.get("/stats/dashboard")
      ]);
      setOrders(ordersRes.data || []);
      setStats(statsRes.data || { total: 0, pending: 0, ordered: 0, completed: 0 });
      setRefreshKey(Date.now());
      toast.success("Refreshed");
    } catch (error) {
      toast.error("Refresh failed - please try again");
    } finally {
      setLoading(false);
    }
  };

  const handleOrderCreated = () => {
    refreshAll();
    setActiveTab("orders");
  };

  const orderMatchesStatus = (order, filterStatus) => {
    if (filterStatus === "all") return true;
    return order.status === filterStatus;
  };

  return (
    <div className="min-h-screen bg-slate-50 pb-20">
      {/* Mobile Header */}
      <header className="bg-white border-b border-slate-200 shadow-sm sticky top-0 z-50">
        <div className="px-4 py-3">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-2">
              <Package className="w-6 h-6 text-violet-600" />
              <div>
                <h1 className="text-lg font-bold text-slate-900">Tech Portal</h1>
                <p className="text-xs text-slate-500">{user.full_name}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="ghost" size="sm" onClick={refreshAll} className="p-2">
                <RefreshCw className="w-5 h-5" />
              </Button>
              <Button variant="ghost" size="sm" onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="p-2">
                {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
              </Button>
            </div>
          </div>
        </div>

        {/* Mobile Menu Dropdown */}
        {mobileMenuOpen && (
          <div className="absolute top-full left-0 right-0 bg-white border-b shadow-lg z-50">
            <div className="p-4 space-y-2">
              <Button
                variant="outline"
                className="w-full justify-start"
                onClick={() => { setPasswordDialogOpen(true); setMobileMenuOpen(false); }}
              >
                <Key className="w-4 h-4 mr-2" />
                Change Password
              </Button>
              <Button
                variant="outline"
                className="w-full justify-start text-red-600"
                onClick={onLogout}
              >
                <LogOut className="w-4 h-4 mr-2" />
                Logout
              </Button>
            </div>
          </div>
        )}
      </header>

      {/* Quick Stats Bar */}
      {stats && (
        <div className="bg-white border-b px-4 py-3">
          <div className="flex justify-between gap-2 overflow-x-auto">
            <button
              onClick={() => { setStatusFilter("all"); setActiveTab("orders"); }}
              className={`flex-1 min-w-[70px] py-2 px-3 rounded-lg text-center transition-all ${
                statusFilter === "all" ? "bg-violet-100 text-violet-700" : "bg-slate-100 text-slate-600"
              }`}
            >
              <div className="text-lg font-bold">{stats.total_orders || 0}</div>
              <div className="text-xs">All</div>
            </button>
            <button
              onClick={() => { setStatusFilter("pending"); setActiveTab("orders"); }}
              className={`flex-1 min-w-[70px] py-2 px-3 rounded-lg text-center transition-all ${
                statusFilter === "pending" ? "bg-yellow-100 text-yellow-700" : "bg-slate-100 text-slate-600"
              }`}
            >
              <div className="text-lg font-bold">{stats.pending_orders || 0}</div>
              <div className="text-xs">Pending</div>
            </button>
            <button
              onClick={() => { setStatusFilter("ordered"); setActiveTab("orders"); }}
              className={`flex-1 min-w-[70px] py-2 px-3 rounded-lg text-center transition-all ${
                statusFilter === "ordered" ? "bg-blue-100 text-blue-700" : "bg-slate-100 text-slate-600"
              }`}
            >
              <div className="text-lg font-bold">{stats.ordered_count || stats.ordered_orders || 0}</div>
              <div className="text-xs">Ordered</div>
            </button>
            <button
              onClick={() => { setStatusFilter("delivered"); setActiveTab("orders"); }}
              className={`flex-1 min-w-[70px] py-2 px-3 rounded-lg text-center transition-all ${
                statusFilter === "delivered" ? "bg-green-100 text-green-700" : "bg-slate-100 text-slate-600"
              }`}
            >
              <div className="text-lg font-bold">{stats.completed_orders || 0}</div>
              <div className="text-xs">Done</div>
            </button>
          </div>
        </div>
      )}

      {/* Main Content */}
      <main className="px-4 py-4">
        {/* Orders Tab */}
        {activeTab === "orders" && (
          <div>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold">
                {statusFilter === "all" ? "All Orders" : 
                 statusFilter === "pending" ? "Pending" :
                 statusFilter === "ordered" ? "Ordered" :
                 statusFilter === "delivered" ? "Delivered" : "Orders"}
              </h2>
              {loading && <Loader2 className="w-5 h-5 animate-spin text-violet-600" />}
            </div>
            
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-violet-600" />
              </div>
            ) : (
              <OrdersList 
                key={`orders-${refreshKey}`}
                orders={orders.filter(order => orderMatchesStatus(order, statusFilter))} 
                onRefresh={refreshAll} 
                user={user} 
              />
            )}
          </div>
        )}

        {/* New Order Tab */}
        {activeTab === "create" && (
          <div>
            <h2 className="text-lg font-semibold mb-4">New Order</h2>
            <OrderFormMultiPart onOrderCreated={handleOrderCreated} user={user} />
          </div>
        )}

        {/* AI Diagnostic Tab */}
        {activeTab === "diagnostic" && (
          <div>
            <h2 className="text-lg font-semibold mb-4">AI Diagnostic</h2>
            <p className="text-sm text-slate-600 mb-4">
              Generate professional diagnostic notes for customer invoices
            </p>
            <DiagnosticGenerator />
          </div>
        )}

        {/* Calculator Tab */}
        {activeTab === "calculator" && (
          <div>
            <h2 className="text-lg font-semibold mb-4">Price Calculator</h2>
            <MarkupCalculator />
          </div>
        )}

        {/* Truck Inventory Tab */}
        {activeTab === "truck" && (
          <TruckInventory user={user} />
        )}
      </main>

      {/* Bottom Navigation - Mobile Optimized */}
      <nav className="fixed bottom-0 left-0 right-0 bg-white border-t border-slate-200 shadow-lg z-50 safe-area-inset-bottom">
        <div className="flex justify-around items-center py-2 px-1">
          <button
            onClick={() => setActiveTab("orders")}
            className={`flex flex-col items-center py-2 px-4 rounded-lg transition-colors ${
              activeTab === "orders" ? "text-violet-600 bg-violet-50" : "text-slate-500"
            }`}
          >
            <Home className="w-5 h-5" />
            <span className="text-[10px] mt-1">Orders</span>
          </button>
          <button
            onClick={() => setActiveTab("create")}
            className={`flex flex-col items-center py-2 px-4 rounded-lg transition-colors ${
              activeTab === "create" ? "text-violet-600 bg-violet-50" : "text-slate-500"
            }`}
          >
            <Plus className="w-5 h-5" />
            <span className="text-[10px] mt-1">New</span>
          </button>
          <button
            onClick={() => setActiveTab("diagnostic")}
            className={`flex flex-col items-center py-2 px-4 rounded-lg transition-colors ${
              activeTab === "diagnostic" ? "text-violet-600 bg-violet-50" : "text-slate-500"
            }`}
          >
            <Sparkles className="w-5 h-5" />
            <span className="text-[10px] mt-1">AI</span>
          </button>
          <button
            onClick={() => setActiveTab("calculator")}
            className={`flex flex-col items-center py-2 px-4 rounded-lg transition-colors ${
              activeTab === "calculator" ? "text-violet-600 bg-violet-50" : "text-slate-500"
            }`}
          >
            <Calculator className="w-5 h-5" />
            <span className="text-[10px] mt-1">Calc</span>
          </button>
          <button
            onClick={() => setActiveTab("truck")}
            className={`flex flex-col items-center py-2 px-4 rounded-lg transition-colors ${
              activeTab === "truck" ? "text-violet-600 bg-violet-50" : "text-slate-500"
            }`}
            data-testid="nav-truck"
          >
            <Truck className="w-5 h-5" />
            <span className="text-[10px] mt-1">Truck</span>
          </button>
        </div>
      </nav>

      <ChangePasswordDialog 
        open={passwordDialogOpen} 
        onOpenChange={setPasswordDialogOpen} 
      />
    </div>
  );
}
